<template>
  <span class="avatar-chip" :style="chipStyle">
    <!-- A picture supersedes the initial; the gradient stays underneath it,
         which is what shows while it loads and what is left if it never
         arrives. -->
    <img
      v-if="showsImage"
      class="avatar-chip__image"
      :src="imageUrl"
      :alt="imageAlt"
      @error="imageFailed = true"
    >
    <template v-else>{{ initial }}</template>
    <span v-if="online" class="avatar-chip__online-indicator" />
  </span>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { avatarGradient } from '../../utils/formatters';
import { isAbsentOrNonBlankString } from '../../utils/propValidators';

// One person, drawn as a picture when there is one and as an initial when there
// is not — on a gradient keyed to a stable seed, so the same person keeps one
// color on every surface they appear. Five surfaces drew this by hand before —
// the inbox row, the profile header, the conversation header, the compose row
// and the notification banner — each with its own class block and its own call
// to `avatarGradient`. The seed is the only thing a caller must pass.
//
// There is no slot. There was one, and it was the image path; `imageUrl` is
// that path now, and two ways to fill one circle is a precedence rule nobody
// asked for.
defineOptions({ name: 'AvatarChip' });

const props = defineProps({
  // Usually one or two letters. Empty renders a bare gradient, which is the
  // correct look for an unnamed or still-loading entity.
  initial: { type: String, default: '' },
  // Any stable key for this entity — an id is preferred over a name, since a
  // name can change and would move the color with it.
  seed: { type: [String, Number], default: '' },
  // Diameter in px. The type scales with it, so callers set one number.
  size: { type: Number, default: 40 },
  // The small corner dot used by the conversation header for presence.
  online: { type: Boolean, default: false },
  // A photograph of this person. Absent for most of them, and absent is not a
  // failure: the initial on its gradient is the designed answer, not a
  // placeholder for this. Same name, default and validator as BidCard's and
  // BidSheet's.
  imageUrl: { type: String, default: null, validator: isAbsentOrNonBlankString },
  // Empty on purpose. Every call site writes the person's name beside the chip,
  // so an alt here would be the name said twice; the component knows only the
  // initial, and announcing "D" helps nobody. Pass one when the chip stands
  // alone with no name near it.
  imageAlt: { type: String, default: '' },
});

// A URL that 404s is not a picture the chip has. Without this the failure is a
// broken-image glyph on the gradient — worse than the letter it replaced.
const imageFailed = ref(false);

// Re-arm on a new URL. Without this one bad picture poisons the chip for every
// person rendered through it afterwards, which is what a recycled list row does
// — and a test that re-mounts instead of re-propping will never see it.
watch(() => props.imageUrl, () => { imageFailed.value = false; });

const showsImage = computed(() => Boolean(props.imageUrl) && !imageFailed.value);

// 0.34 of the diameter tracks what the hand-built copies used at every size
// they appeared in (13px at 38, 13px at 40, 16px at 46), so unifying on it
// leaves the small avatars where they were and only nudges the 96px profile
// avatar, which had drifted a little small for its circle.
const chipStyle = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
  borderRadius: `${props.size}px`,
  fontSize: `${Math.round(props.size * 0.34)}px`,
  backgroundImage: avatarGradient(props.seed),
}));
</script>

<style scoped>
.avatar-chip {
  position: relative;
  flex: none;
  display: grid;
  place-items: center;
  color: var(--p-avatar-chip-color);
  font-family: var(--p-avatar-chip-font-family);
  font-weight: var(--p-avatar-chip-font-weight);
  line-height: var(--p-avatar-chip-line-height);
  /* Deliberately NOT `overflow: hidden`. The circle is drawn by the inline
     border radius, and the image carries its own — clipping here would take the
     presence dot with it, because the dot sits at the corner of the box, which
     is outside the circle. */
  user-select: none;
}

/* Fills the circle and crops rather than distorts: avatars arrive at whatever
   aspect ratio the person uploaded. `inherit` follows the inline radius from
   chipStyle, so one number still governs the whole shape. */
.avatar-chip__image {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  object-fit: cover;
}

.avatar-chip__online-indicator {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 10px;
  height: 10px;
  border-radius: var(--p-avatar-chip-online-indicator-border-radius);
  background: var(--p-avatar-chip-online-indicator-background);
  border: var(--p-avatar-chip-online-indicator-border-width) solid var(--p-avatar-chip-online-indicator-border-color);
}
</style>
